// -----------------------------------------------------------------------------
// G-SCORE STUB FIXTURE
//
// Mock API *responses* only (GSCORE_DASHBOARD_STUB, GSCORE_HISTORY_STUB) —
// source of truth for the G-Score API shape while the backend is being built.
// When the real endpoint ships, flip USE_STUB=false in the hooks; this file
// is then safe to delete (or keep for offline development). Any divergence
// between this fixture and the eventual backend response is a spec bug.
//
// Real, permanent config (PILLARS, TRAINING_GAMES, ASSESSMENT_TASKS) lives in
// ../constants.js, not here — that config doesn't go away when the backend
// ships, so it must never be deleted alongside this file.
//
// Load-bearing invariant: scores here come only from the (fake) assessment
// battery, never from training-game performance. The backend must enforce
// the same separation.
// -----------------------------------------------------------------------------

// Compute the next-assessment ISO based on lastAssessedAt + 14 days.
function addDays(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

const NOW = new Date().toISOString();
const SIX_DAYS_AGO = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d.toISOString();
})();

// The main dashboard payload. Everything the /g-score landing page renders
// comes out of this response.
export const GSCORE_DASHBOARD_STUB = {
  // Composite G-Score (0-100). Computed from pillar scores; frontend does
  // not recompute — this is authoritative from the assessment pipeline.
  composite: 78,
  compositePercentile: 66, // vs. age band
  compositeTrend: 'up',    // 'up' | 'down' | 'flat'
  compositeDelta: 3,       // change since last assessment; sign matches trend

  // Per-pillar breakdown. Same 0-100 scale. `subscores` optional and reserved
  // for V2 (Attention has 3 sub-components, Memory has 2, etc.).
  pillars: [
    { id: 'speed',       score: 82, percentile: 71, trend: 'up',   delta: 4,  lastAssessedAt: SIX_DAYS_AGO, subscores: null },
    { id: 'attention',   score: 76, percentile: 62, trend: 'flat', delta: 0,  lastAssessedAt: SIX_DAYS_AGO, subscores: null },
    { id: 'memory',      score: 71, percentile: 54, trend: 'down', delta: -2, lastAssessedAt: SIX_DAYS_AGO, subscores: null },
    { id: 'flexibility', score: 74, percentile: 58, trend: 'up',   delta: 2,  lastAssessedAt: SIX_DAYS_AGO, subscores: null },
    { id: 'problem',     score: 80, percentile: 70, trend: 'up',   delta: 5,  lastAssessedAt: SIX_DAYS_AGO, subscores: null },
  ],

  // Assessment cadence + windowing. Frontend renders a countdown and unlocks
  // the "Take assessment now" CTA when nextAssessmentAvailableAt <= now.
  assessment: {
    lastCompletedAt: SIX_DAYS_AGO,
    nextAvailableAt: addDays(SIX_DAYS_AGO, 14), // biweekly
    cadenceDays: 14,
    // If the user misses their window, the backend still allows a run — this
    // just controls the "on schedule vs. overdue" badge.
    overdue: false,
  },

  // Streak measured across daily training-game sessions (not assessment).
  streak: {
    currentDays: 4,
    longestDays: 11,
    trainedToday: false,
    // 14-day activity strip (oldest → newest). Boolean per day.
    recentDays: [true, true, false, true, true, false, false, true, true, true, true, false, true, false],
  },

  // Today's recommended routine — 3 games, ~7 min total.
  // Backend picks these adaptively per-user; frontend renders as given.
  todayRoutine: {
    generatedAt: NOW,
    games: [
      { id: 'n-back',     targetPillar: 'memory',       recommendedDifficulty: 3, durationSeconds: 180 },
      { id: 'flanker',    targetPillar: 'attention',    recommendedDifficulty: 4, durationSeconds: 150 },
      { id: 'arithmetic', targetPillar: 'problem',      recommendedDifficulty: 5, durationSeconds: 120 },
    ],
    completedIds: [], // ids the user has already done in this routine
  },

  // Small activity feed for the dashboard footer.
  recentActivity: [
    { kind: 'training',   ref: 'n-back',    at: SIX_DAYS_AGO,             note: 'Reached difficulty 3' },
    { kind: 'assessment', ref: 'battery',   at: SIX_DAYS_AGO,             note: 'Composite +3' },
    { kind: 'training',   ref: 'stroop',    at: addDays(SIX_DAYS_AGO, 2), note: '94% accuracy' },
  ],
};

// History of assessment runs. Immutable — each row is a snapshot of the
// user's composite + pillar scores at the moment the assessment finalized.
// Sorted oldest → newest. Backend authoritative on storage; this fixture
// mirrors the shape the /history endpoint should return.
export const GSCORE_HISTORY_STUB = {
  entries: [
    {
      runId: 'stub-run-01',
      takenAt: addDays(SIX_DAYS_AGO, -14 * 8), // ~4 months ago
      composite: 61,
      compositePercentile: 42,
      pillars: [
        { id: 'speed',       score: 65 },
        { id: 'attention',   score: 58 },
        { id: 'memory',      score: 60 },
        { id: 'flexibility', score: 63 },
        { id: 'problem',     score: 62 },
      ],
    },
    {
      runId: 'stub-run-02',
      takenAt: addDays(SIX_DAYS_AGO, -14 * 7),
      composite: 64,
      compositePercentile: 48,
      pillars: [
        { id: 'speed',       score: 68 },
        { id: 'attention',   score: 62 },
        { id: 'memory',      score: 61 },
        { id: 'flexibility', score: 65 },
        { id: 'problem',     score: 66 },
      ],
    },
    {
      runId: 'stub-run-03',
      takenAt: addDays(SIX_DAYS_AGO, -14 * 6),
      composite: 68,
      compositePercentile: 54,
      pillars: [
        { id: 'speed',       score: 72 },
        { id: 'attention',   score: 66 },
        { id: 'memory',      score: 64 },
        { id: 'flexibility', score: 68 },
        { id: 'problem',     score: 71 },
      ],
    },
    {
      runId: 'stub-run-04',
      takenAt: addDays(SIX_DAYS_AGO, -14 * 5),
      composite: 71,
      compositePercentile: 58,
      pillars: [
        { id: 'speed',       score: 76 },
        { id: 'attention',   score: 69 },
        { id: 'memory',      score: 66 },
        { id: 'flexibility', score: 70 },
        { id: 'problem',     score: 74 },
      ],
    },
    {
      runId: 'stub-run-05',
      takenAt: addDays(SIX_DAYS_AGO, -14 * 4),
      composite: 72,
      compositePercentile: 60,
      pillars: [
        { id: 'speed',       score: 77 },
        { id: 'attention',   score: 71 },
        { id: 'memory',      score: 68 },
        { id: 'flexibility', score: 71 },
        { id: 'problem',     score: 74 },
      ],
    },
    {
      runId: 'stub-run-06',
      takenAt: addDays(SIX_DAYS_AGO, -14 * 3),
      composite: 74,
      compositePercentile: 62,
      pillars: [
        { id: 'speed',       score: 79 },
        { id: 'attention',   score: 73 },
        { id: 'memory',      score: 70 },
        { id: 'flexibility', score: 72 },
        { id: 'problem',     score: 77 },
      ],
    },
    {
      runId: 'stub-run-07',
      takenAt: addDays(SIX_DAYS_AGO, -14 * 2),
      composite: 75,
      compositePercentile: 63,
      pillars: [
        { id: 'speed',       score: 80 },
        { id: 'attention',   score: 74 },
        { id: 'memory',      score: 69 },
        { id: 'flexibility', score: 72 },
        { id: 'problem',     score: 76 },
      ],
    },
    {
      runId: 'stub-run-08',
      takenAt: SIX_DAYS_AGO, // Latest — matches the dashboard's current values
      composite: 78,
      compositePercentile: 66,
      pillars: [
        { id: 'speed',       score: 82 },
        { id: 'attention',   score: 76 },
        { id: 'memory',      score: 71 },
        { id: 'flexibility', score: 74 },
        { id: 'problem',     score: 80 },
      ],
    },
  ],
};
